/**
 * Robust cross-browser download trigger.
 *
 * Why a helper: the naive `<a download>.click()` pattern works in Chrome but
 * silently does nothing in Firefox/Safari if the anchor isn't in the document,
 * and intermittently fails when the object URL is revoked too early. Browsers
 * also enforce a "user gesture" rule — async/await between the click and the
 * actual download often kills the trigger in Chrome with strict ad-blockers.
 *
 * This helper handles all three cases:
 *   1. If you pass a server URL + filename → builds a plain anchor with
 *      `?token=<jwt>` and clicks it synchronously inside the user gesture.
 *      No fetch, no Blob, no object URL — the browser handles it natively.
 *      This is the bullet-proof path; use it for backend-served templates.
 *   2. If you pass a Blob or Promise → falls back to the older blob-URL flow
 *      with proper DOM-attach + deferred revoke.
 */
import { API } from "@/lib/api";

export function downloadFromServer(pathOrUrl, filename) {
  const token = localStorage.getItem("pm_token") || "";
  // pathOrUrl can be either "/admin/foo" (relative to API) or a full URL.
  const base = pathOrUrl.startsWith("http") ? pathOrUrl : `${API}${pathOrUrl}`;
  const sep = base.includes("?") ? "&" : "?";
  const href = token ? `${base}${sep}token=${encodeURIComponent(token)}` : base;
  const a = document.createElement("a");
  a.href = href;
  a.download = filename || "download";
  a.rel = "noopener";
  // target=_blank makes Safari open the response in a temporary tab that
  // immediately auto-closes when Content-Disposition is attachment — much
  // more reliable than same-tab on iOS.
  a.target = "_blank";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { try { a.remove(); } catch { /* noop */ } }, 1200);
}

export async function downloadBlob(source, filename, mimeHint) {
  let blob;
  if (source instanceof Blob) {
    blob = source;
  } else if (source && typeof source.then === "function") {
    const r = await source;
    if (r instanceof Response) {
      if (!r.ok) throw new Error(`Download failed: ${r.status}`);
      blob = await r.blob();
    } else if (r && r.data instanceof Blob) {
      blob = r.data;
    } else if (r && r.data != null) {
      blob = new Blob([r.data], mimeHint ? { type: mimeHint } : undefined);
    } else {
      throw new Error("Download source returned no data");
    }
  } else {
    throw new Error("Unsupported download source");
  }
  if (mimeHint && !blob.type) {
    blob = new Blob([blob], { type: mimeHint });
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    try { a.remove(); } catch { /* noop */ }
    try { URL.revokeObjectURL(url); } catch { /* noop */ }
  }, 1200);
}
